/**
 * Countdown Timer Class
 * Handles countdown functionality for timer sections
 */
class CountdownTimer {
  constructor(element, targetDate) {
    this.element = element;
    this.targetDate = targetDate;
    this.hoursElement = element.querySelector("[data-hours]");
    this.minutesElement = element.querySelector("[data-minutes]");
    this.secondsElement = element.querySelector("[data-seconds]");

    // Create a unique storage key for this timer
    this.storageKey = `timer_${element.getAttribute("data-target-date")}_${
      element.closest(".timer-section, .cart-timer-section")?.id || "default"
    }`;

    // Check if we have a stored countdown state
    this.loadStoredState();

    this.init();
  }

  loadStoredState() {
    const now = new Date().getTime();
    const originalTarget = this.targetDate.getTime();

    // Check if original target date is in the past
    if (originalTarget <= now) {
      // Try to load stored state
      const storedState = localStorage.getItem(this.storageKey);
      if (storedState) {
        const { targetTime, lastUpdate } = JSON.parse(storedState);
        const timeSinceLastUpdate = now - lastUpdate;
        const remainingTime = targetTime - timeSinceLastUpdate;

        if (remainingTime > 0) {
          // Continue from stored state
          this.targetDate = new Date(targetTime);
          return;
        }
      }

      // No valid stored state or time expired, start new countdown
      this.targetDate = new Date(now + 48 * 60 * 60 * 1000);
      this.saveState();
    }
  }

  saveState() {
    const state = {
      targetTime: this.targetDate.getTime(),
      lastUpdate: new Date().getTime(),
    };
    localStorage.setItem(this.storageKey, JSON.stringify(state));
  }

  init() {
    this.updateTimer();
    this.interval = setInterval(() => {
      this.updateTimer();
    }, 1000);
  }

  updateTimer() {
    const now = new Date().getTime();
    const target = this.targetDate.getTime();
    const difference = target - now;

    if (difference > 0) {
      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      this.hoursElement.textContent = this.formatNumber(hours);
      this.minutesElement.textContent = this.formatNumber(minutes);
      this.secondsElement.textContent = this.formatNumber(seconds);

      // Save state every 10 seconds to avoid too frequent localStorage writes
      if (seconds % 10 === 0) {
        this.saveState();
      }
    } else {
      // Timer has expired - reset to 48 hours
      this.hoursElement.textContent = "48";
      this.minutesElement.textContent = "00";
      this.secondsElement.textContent = "00";

      // Set new target date to 48 hours from now
      this.targetDate = new Date(now + 48 * 60 * 60 * 1000);

      // Save the new state
      this.saveState();

      // Dispatch custom event for timer reset
      this.element.dispatchEvent(
        new CustomEvent("timerReset", {
          detail: { newTargetDate: this.targetDate },
        })
      );
    }
  }

  formatNumber(num) {
    return num.toString().padStart(2, "0");
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}

// Initialize all timer sections on page load
document.addEventListener("DOMContentLoaded", function () {
  // Handle timer-section__countdown elements
  const timerSections = document.querySelectorAll(".timer-section__countdown");
  timerSections.forEach((timerElement) => {
    const targetDateString = timerElement.getAttribute("data-target-date");
    if (targetDateString) {
      try {
        const targetDate = new Date(targetDateString);
        if (!isNaN(targetDate.getTime())) {
          new CountdownTimer(timerElement, targetDate);
        } else {
          console.error("Invalid date format for timer:", targetDateString);
        }
      } catch (error) {
        console.error("Error initializing timer:", error);
      }
    }
  });

  // Handle cart-timer-section__countdown elements
  const cartTimerSections = document.querySelectorAll(
    ".cart-timer-section__countdown"
  );
  cartTimerSections.forEach((timerElement) => {
    const targetDateString = timerElement.getAttribute("data-target-date");
    if (targetDateString) {
      try {
        const targetDate = new Date(targetDateString);
        if (!isNaN(targetDate.getTime())) {
          new CountdownTimer(timerElement, targetDate);
        } else {
          console.error(
            "Invalid date format for cart timer:",
            targetDateString
          );
        }
      } catch (error) {
        console.error("Error initializing cart timer:", error);
      }
    }
  });
});

// Handle timer reset globally
document.addEventListener("timerReset", function (event) {
  console.log("Timer reset to new target date:", event.detail.newTargetDate);

  // You can add custom behavior here when timer resets
  // For example: show a message, change styling, etc.
  const timerSection = event.target.closest(
    ".timer-section, .cart-timer-section"
  );
  if (timerSection) {
    // Optional: Add visual feedback when timer resets
    timerSection.style.transition = "opacity 0.3s ease";
    timerSection.style.opacity = "0.8";
    setTimeout(() => {
      timerSection.style.opacity = "1";
    }, 300);
  }
});
